import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, TrendingUp, ArrowUpRight, Scale, CreditCard } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token')}` } });
const usd = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminPlatformFinancials() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/platform-financials`, auth())
      .then(r => setData(r.data))
      .catch(e => toast.error(e.response?.data?.detail || 'Could not load platform financials'));
  }, []);

  if (!data) return <div className="min-h-screen bg-background pt-28 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>;

  const { revenue, monthly, top_events, money_out, liabilities, stripe_balance } = data;
  const out30 = money_out.last_30d; const outAll = money_out.all_time;
  const outRows = [
    ['Organizer bank withdrawals', 'organizer_withdrawals'],
    ['Staff / vendor / venue payments', 'staff_vendor_payments'],
    ['Wallet credits sent', 'wallet_credits_sent'],
    ['User bank withdrawals', 'user_bank_withdrawals'],
  ];
  const totalOutAll = outRows.reduce((s, [, k]) => s + (outAll[k]?.cents || 0), 0);
  const totalOut30 = outRows.reduce((s, [, k]) => s + (out30[k]?.cents || 0), 0);

  return (
    <div className="min-h-screen bg-background pt-24 pb-16">
      <div className="max-w-5xl mx-auto px-4 space-y-6" data-testid="platform-financials-page">
        <div>
          <h1 className="text-2xl font-extrabold flex items-center gap-2"><TrendingUp className="h-6 w-6 text-emerald-400" /> Eventism Platform Financials</h1>
          <p className="text-sm text-muted-foreground">What the platform earns, pays out, and owes — across every event and partner.</p>
        </div>

        <div>
          <p className="text-sm font-semibold mb-2">Platform revenue (service fees)</p>
          <div className="grid sm:grid-cols-3 gap-3">
            {[['All time', revenue.all_time], ['Last 30 days', revenue.last_30d], ['Last 7 days', revenue.last_7d]].map(([label, r]) => (
              <Card key={label} className="border-emerald-500/30"><CardContent className="py-3">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
                <p className="text-2xl font-extrabold text-emerald-400" data-testid={`rev-${label.toLowerCase().replace(/ /g, '-')}`}>{usd(r.fees_cents)}</p>
                <p className="text-[11px] text-muted-foreground">{r.orders.toLocaleString()} orders · {usd(r.gross_cents)} gross sales</p>
              </CardContent></Card>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-semibold mb-2">Monthly fees (12 months)</p>
            <div className="rounded-lg border border-border overflow-x-auto">
              <table className="w-full text-sm" data-testid="monthly-table">
                <thead><tr className="bg-muted/40"><th className="text-left px-3 py-2">Month</th><th className="text-right px-3 py-2">Fees earned</th><th className="text-right px-3 py-2">Gross</th><th className="text-right px-3 py-2">Orders</th></tr></thead>
                <tbody>
                  {monthly.length === 0 ? <tr><td colSpan={4} className="px-3 py-5 text-center text-muted-foreground text-xs">No data yet.</td></tr>
                    : [...monthly].reverse().map(m => (
                      <tr key={m.month} className="border-t border-border/60">
                        <td className="px-3 py-1.5">{m.month}</td>
                        <td className="px-3 py-1.5 text-right font-semibold text-emerald-400">{usd(m.fees)}</td>
                        <td className="px-3 py-1.5 text-right">{usd(m.gross)}</td>
                        <td className="px-3 py-1.5 text-right">{m.orders}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold mb-2">Top events by fees earned</p>
            <div className="rounded-lg border border-border overflow-x-auto">
              <table className="w-full text-sm" data-testid="top-events-table">
                <thead><tr className="bg-muted/40"><th className="text-left px-3 py-2">Event</th><th className="text-right px-3 py-2">Fees</th><th className="text-right px-3 py-2">Gross</th><th className="text-right px-3 py-2">Orders</th></tr></thead>
                <tbody>
                  {top_events.map(t => (
                    <tr key={t.event_id || t.event_name} className="border-t border-border/60">
                      <td className="px-3 py-1.5 text-xs">{t.event_name}</td>
                      <td className="px-3 py-1.5 text-right font-semibold text-emerald-400">{usd(t.fees)}</td>
                      <td className="px-3 py-1.5 text-right">{usd(t.gross)}</td>
                      <td className="px-3 py-1.5 text-right">{t.orders}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div>
          <p className="text-sm font-semibold mb-2 flex items-center gap-1.5"><ArrowUpRight className="h-4 w-4 text-rose-400" /> Money out</p>
          <div className="rounded-lg border border-border overflow-x-auto">
            <table className="w-full text-sm" data-testid="money-out-table">
              <thead><tr className="bg-muted/40"><th className="text-left px-3 py-2">Type</th><th className="text-right px-3 py-2">Last 30 days</th><th className="text-right px-3 py-2">All time</th></tr></thead>
              <tbody>
                {outRows.map(([label, k]) => (
                  <tr key={k} className="border-t border-border/60">
                    <td className="px-3 py-1.5">{label}</td>
                    <td className="px-3 py-1.5 text-right">{usd(out30[k]?.cents)} <span className="text-[10px] text-muted-foreground">({out30[k]?.count || 0})</span></td>
                    <td className="px-3 py-1.5 text-right font-semibold text-rose-400">{usd(outAll[k]?.cents)} <span className="text-[10px] text-muted-foreground">({outAll[k]?.count || 0})</span></td>
                  </tr>
                ))}
                <tr className="border-t border-border bg-muted/20 font-bold">
                  <td className="px-3 py-1.5">Total out</td>
                  <td className="px-3 py-1.5 text-right">{usd(totalOut30)}</td>
                  <td className="px-3 py-1.5 text-right text-rose-400">{usd(totalOutAll)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <Card className="border-amber-500/30"><CardContent className="py-4 space-y-1">
            <p className="text-sm font-semibold flex items-center gap-1.5"><Scale className="h-4 w-4 text-amber-400" /> Wallet liabilities (you owe)</p>
            <p className="text-2xl font-extrabold text-amber-400" data-testid="liabilities-total">{usd(liabilities.total_cents)}</p>
            <p className="text-[11px] text-muted-foreground">Organizer wallets: {usd(liabilities.org_wallets_cents)} ({liabilities.org_wallets_count}) · User wallets: {usd(liabilities.user_wallets_cents)} ({liabilities.user_wallets_count})</p>
          </CardContent></Card>
          <Card className="border-indigo-500/30"><CardContent className="py-4 space-y-1">
            <p className="text-sm font-semibold flex items-center gap-1.5"><CreditCard className="h-4 w-4 text-indigo-400" /> Live Stripe balance</p>
            {stripe_balance ? (
              <>
                <p className="text-2xl font-extrabold" data-testid="stripe-available">{usd(stripe_balance.available_cents)} <span className="text-xs font-normal text-muted-foreground">available</span></p>
                <p className="text-[11px] text-muted-foreground">Pending from recent sales: {usd(stripe_balance.pending_cents)}</p>
              </>
            ) : <p className="text-xs text-muted-foreground">Stripe balance unavailable.</p>}
          </CardContent></Card>
        </div>
      </div>
    </div>
  );
}
